import {PublicTicketsFormDefs} from "../PublicTicketsForm";
import {ShowSelectStage} from "../Stages/ShowSelectStage";
import {DateSelectStage} from "../Stages/DateSelectStage";
import {GlobalState} from "../State/GlobalState";
import {ReservedSeatsSelectStage} from "../Stages/ReservedSeatsSelectStage";
import {TicketTypeSelectStage} from "../Stages/TicketTypeSelectStage";
import {PaymentInfoStage} from "../Stages/PaymentInfoStage";
import {OrderSummaryStage} from "../Stages/OrderSummaryStage";

class StageManager {
    /** @var {GlobalState} */
    globalState;
    /**
     * Here we keep track of all initialized stage instances
     *
     * @var {Object<PublicTicketsFormDefs.Stage,BaseStage>}
     */
    stageInstances;

    initialBrowserHistoryLength

    constructor(globalState) {
        this.globalState = globalState;

        // We store the initial browser history length so we know
        // the point where we cannot pop back
        this.initialBrowserHistoryLength = window.history.length;

        // Init empty stage instances cache
        // noinspection JSValidateTypes
        this.stageInstances = {};

        // Prevent the user from leaving if we are past the first stage
        window.onbeforeunload = () => {
            if (Object.keys(this.stageInstances).length > 1) {
                console.log('Warning the user before he leaves');
                return true;
            }
        }

        // Take care of displaying the proper stage when the user
        // presses back on the browser
        window.onpopstate = (ev) => {
            this.handleBrowserHistoryPopState(ev);
        }
    }

    nextStage() {
        const nextStageIndex = this.getCurrentStageIndex() + 1;
        if (nextStageIndex >= this.globalState.stageArray.length) {
            // Out of bounds
            return;
        }

        // Save current stage and re render
        this.globalState.currentStage = this.globalState.stageArray[nextStageIndex];
        this.renderStage();
    };

    previousStage() {
        let previousStageIndex = this.getCurrentStageIndex() - 1;
        if (previousStageIndex < 0) {
            // Out of bounds
            return;
        }

        // If we are at ticket type stage for a general admission order...
        if (
            this.globalState.currentStage === 'seat-type-select' &&
            this.globalState.selectedSeatType === 'general-admission'
        ) {
            // ... skip an extra step to bypass seat selection stage
            previousStageIndex -= 1;
        }

        // Save current stage and re render
        this.globalState.currentStage = this.globalState.stageArray[previousStageIndex];
        this.renderStage();
    };

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    goToStage(stage) {
        this.globalState.currentStage = stage;

        this.renderStage();
    };

    getCurrentStageIndex() {
        return this.globalState.stageArray.indexOf(this.globalState.currentStage);
    }

    getStagesContainer() {
        return document.querySelector('.stages-container');
    }

    /**
     * Renders current stage into the DOM
     */
    renderStage() {
        const state = this.globalState;

        let newStageInstance = false;

        // Create instance if not initialized
        if (!this.stageInstances[state.currentStage]) {
            this.stageInstances[state.currentStage] = this.getStageInstance(state.currentStage)
            newStageInstance = true;
        }

        /** @var {BaseStage} */
        const stageInstance = this.stageInstances[state.currentStage];

        // Hide all stages
        this.getStagesContainer().querySelectorAll('.stage').forEach(stage => {
            stage.classList.add('d-none');
        })

        // Display current stage
        stageInstance.display();

        // If we are showing a new stage instance, scroll up
        if (newStageInstance) {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant',
            })
        }

        this.updateBrowserHistory();
    }

    /**
     * Initializes a stage instance based on the current global state
     *
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    getStageInstance(stage) {
        const state = this.globalState;

        switch (stage) {
            case "show-select":
                return new ShowSelectStage(this.globalState);
            case "date-select":
                // Can't go to date stage without having selected a show first
                if (!state.parentEvent) {
                    setTimeout(() => {
                        // Redirect to show select stage
                        this.goToStage('show-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a show first');
                    throw 'Invalid stage change request'
                }

                return new DateSelectStage(this.globalState);
            case "seat-select":
                if (
                    !state.selectedDate ||
                    !state.selectedSeatType
                ) {
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('date-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a date first');
                    throw 'Invalid stage change request'
                }

                if (state.selectedSeatType === 'reserved') {
                    return new ReservedSeatsSelectStage(this.globalState);
                } else {
                    // TODO: Can we do this without the exception?
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('seat-type-select')
                    });

                    throw 'Skipping To Ticket Type Selection (Not an error)'
                }
            case "seat-type-select":
                // Redirect to date select stage
                if (!state.selectedSeatType) {
                    setTimeout(() => {
                        this.goToStage('date-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a date first');
                    throw 'Invalid stage change request';
                }


                // Redirect to seat selects if non set
                if (state.selectedSeatType === 'reserved' && state.selectedSeats.length === 0) {
                    setTimeout(() => {
                        this.goToStage('seat-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select seats first');
                    throw 'Invalid stage change request';
                }

                return new TicketTypeSelectStage(this.globalState);
            case "payment-info":
                if (this.globalState.selectedSeatType === 'reserved') {
                    let reservedTicketTotalCount = 0;

                    this.globalState.reserveTicketCounts.forEach(ticket => {
                        reservedTicketTotalCount += ticket.count
                    })

                    // Only enable next step if total tickets
                    // matches number of selected seats
                    if (reservedTicketTotalCount !== this.globalState.selectedSeats.length) {
                        setTimeout(() => {
                            // Redirect to date select stage
                            this.goToStage('seat-type-select')
                        });

                        // Warn user and end early, this should never happen anyway
                        alert('Please select your tickets first');
                        throw 'Invalid stage change request'
                    }
                } else {

                    if (this.globalState.generalAdmissionTicketCounts.length === 0) {
                        setTimeout(() => {
                            // Redirect to date select stage
                            this.goToStage('seat-type-select')
                        });

                        // Warn user and end early, this should never happen anyway
                        alert('Please select your tickets first');
                        throw 'Invalid stage change request'
                    }
                }

                return new PaymentInfoStage(this.globalState);
            case "summary":
                if (!this.globalState.paymentInfo) {
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('payment-info')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please add your payment info first');
                    throw 'Invalid stage change request'

                }

                return new OrderSummaryStage(this.globalState);
        }
    }

    /**
     * Clear a cached stage instance
     *
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    clearStageInstance(stage) {
        /** @var {BaseStage} */
        const instance = this.stageInstances[stage];

        if (instance) {
            // Clear container
            instance.clearDom();
            delete this.stageInstances[stage];
        }
    }

    updateBrowserHistory() {
        const newStage = this.globalState.currentStage;

        // Push current stage key to history
        window.history.pushState(
            {
                stage: newStage,
            },
            '',
            `/purchasing/s/${newStage}`
        )
    }

    /**
     * @param {PopStateEvent} event
     */
    handleBrowserHistoryPopState(event) {
        if (!event.state || !event.state.stage) {
            // Not our problem, go back
            window.history.back();
        }

        /** @var {PublicTicketsFormDefs.Stage} */
        const requestedStage = event.state.stage;

        const stageExists = this.stageInstances[requestedStage]

        // If the stage does not exists or we are already there
        if (stageExists === false || this.globalState.currentStage === requestedStage) {
            // Keep popping history until the user goes to a valid state
            // or out of the page
            window.history.back();
        } else {
            // Go to stage
            this.goToStage(requestedStage)
        }
    }
}

export {StageManager};
